import { csrf_headers, get_token, set_user_details } from '@/router/index.js';

export const navbar_profile_save_methods = {
  async update_core_user() {
    const token = get_token();
    if (!this.ensure_profile_token(token)) return false;

    const res = await fetch(this.update_core_user_url(), {
      method: 'POST',
      credentials: 'include',
      headers: this.profile_post_headers(token),
      body: JSON.stringify(this.core_user_payload()),
    });
    return this.apply_update_core_user_response(res);
  },

  update_core_user_url() {
    return `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/update_user/`;
  },

  profile_post_headers(token) {
    return ({
      'Content-Type': 'application/json',
      'Authorization': `Token ${token}`,
      'X-Page-URL': window.location.href,
      ...csrf_headers(this.csrfToken),
    });
  },

  core_user_payload() {
    return ({
      first_name: (this.user.first_name || '').trim(),
      last_name: (this.user.last_name || '').trim(),
      email: (this.user.email || '').trim().toLowerCase(),
    });
  },

  async apply_update_core_user_response(res) {
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status !== 'success') {
      this.$notyf.error(this.parse_backend_error(data));
      return false;
    }

    if (data.data) this.user = { ...this.user, ...data.data };
    set_user_details(this.user);
    return true;
  },

  async create_user_profile() {
    const token = get_token();
    if (!this.ensure_profile_token(token)) return;

    try {
      const response = await fetch(this.create_user_profile_url(), {
        method: 'POST',
        credentials: 'include',
        headers: this.profile_post_headers(token),
        body: JSON.stringify(this.user_profile),
      });
      return this.apply_create_user_profile_response(response);
    } catch {
      this.$notyf.error('Error creating profile');
    }
  },

  create_user_profile_url() {
    return `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/create_user_profile/`;
  },

  async apply_create_user_profile_response(response) {
    if (!response.ok) {
      this.$notyf.error('Error creating profile');
      return false;
    }

    this.user_profile = (await response.json()).data;
    this.$notyf.success('User profile created successfully');
    return true;
  },

  async create_company_profile() {
    const token = get_token();
    if (!this.ensure_profile_token(token)) return;

    try {
      const response = await fetch(this.create_company_profile_url(), {
        method: 'POST',
        credentials: 'include',
        headers: this.profile_post_headers(token),
        body: JSON.stringify(this.new_company),
      });
      return this.apply_create_company_profile_response(response);
    } catch {
      this.$notyf.error('Error creating company profile');
    }
  },

  create_company_profile_url() {
    return `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/create_client_company/`;
  },

  async apply_create_company_profile_response(response) {
    const data = await response.json();
    if (!response.ok) {
      this.$notyf.error(data.message || 'Error creating company');
      return false;
    }

    this.new_company = data.data;
    return true;
  },

  async save_changes() {
    if (!this.can_start_profile_save()) return;

    this.saving = true;
    try {
      await this.run_profile_save();
    } finally {
      this.saving = false;
    }
  },

  can_start_profile_save() {
    return !this.registration_error
      && !this.vat_error
      && !this.id_number_error
      && !this.saving;
  },

  async run_profile_save() {
    const changes = this.profile_change_state();
    if (!changes.any) {
      this.finish_no_profile_changes();
      return;
    }

    if (!await this.persist_profile_changes(changes)) return;

    this.edit_mode = false;
    this.$notyf.success('Changes saved successfully');
  },

  organization_changed(is_org) {
    return is_org ? this.company_changed() : false;
  },

  profile_details_changed(is_org) {
    return is_org ? false : this.profile_changed();
  },

  profile_change_state() {
    const is_org = String(
      Reflect.get(this.user ?? {}, 'client_type') ?? ''
    ).toLowerCase() === 'organization';
    const core_changed = this.core_user_changed();
    const org_changed = this.organization_changed(is_org);
    const prof_changed = this.profile_details_changed(is_org);
    return ({
      core_changed,
      org_changed,
      prof_changed,
      any: [core_changed, org_changed, prof_changed].some(Boolean),
    });
  },

  finish_no_profile_changes() {
    this.$notyf.info('No changes to save.');
    this.edit_mode = false;
  },

  async persist_profile_changes(changes) {
    if (!await this.persist_core_changes(changes.core_changed)) return false;
    if (!await this.persist_company_changes(changes.org_changed)) return false;
    if (!await this.persist_user_profile_changes(changes.prof_changed)) return false;

    return true;
  },

  async persist_core_changes(should_save) {
    if (!should_save) return true;
    if (!this.validate_before_save()) return false;
    if (!await this.update_core_user()) return false;

    this.original_user = this.core_user_payload();
    return true;
  },

  async persist_company_changes(should_save) {
    if (!should_save) return true;
    if (!await this.create_company_profile()) return false;

    this.original_company = this.company_snapshot();
    return true;
  },

  async persist_user_profile_changes(should_save) {
    if (!should_save) return true;
    if (!await this.create_user_profile()) return false;

    this.original_profile = this.user_profile_snapshot();
    return true;
  },
};
